import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { LocationMapPicker } from '@/components/business/business-locations/location-map-picker';
import type { RadiusFormValue } from '@/components/business/business-locations/radius-form';
import {
  LocationTargetsTable,
  locationTargetRowLabel,
  toProximityInput,
  useBusinessLocationCandidates,
  useLocationTargetSort,
  type LocationTargetRow,
} from '@/components/campaign/settings/location-targets-table';
import type { Location, CampaignProximityTarget, CampaignProximityInput } from '@/lib/types';
import { CampaignTargetView } from './campaign-target-view';
import { CampaignRadiusTargets } from './campaign-radius-targets';

interface CampaignLocationsSectionProps {
  /** Geo locations — draft.locations. */
  locations: Location[];
  setLocations: (locations: Location[]) => void;
  /** campaign.proximity_targets — read shape, carries the `inherited` flag. */
  currentTargets: CampaignProximityTarget[];
  /** draft.proximityTargets — write shape, undefined/null while untouched. */
  proximityValue: CampaignProximityInput[] | null | undefined;
  onProximityChange: (targets: CampaignProximityInput[]) => void;
  businessId?: number;
  disabled?: boolean;
  campaignName?: string;
  /** #5466: criteria ids already excluded on the campaign — badged in the picker. */
  excludedCriteriaIds?: Set<number>;
}

const toInputList = (targets: CampaignProximityTarget[]): CampaignProximityInput[] =>
  targets.map((t) => ({ latitude: t.latitude, longitude: t.longitude, radius_km: t.radius_km, address: t.address ?? undefined }));

/**
 * Campaign-detail Locations tab (#5452): geo locations and radius targets in ONE
 * table (flex rows, matching the business LocationRow style — truncation, so no
 * horizontal scroll), with a single Add button that opens the shared
 * LocationMapPicker (Locations + Radius tabs). The picker edits the campaign draft
 * live (geo -> target_locations, radius -> proximity_targets), persisted by the
 * page's save bar. The merge is display-only; the two backend fields stay separate.
 * Row model, sort, candidates, and the table itself are shared with Meta's
 * targeting section (#6182) — see `location-targets-table.tsx`.
 *
 * The picker needs a businessId for geocoding and has no read-only mode, so
 * editable businessless campaigns fall back to the original two-table UI.
 */
export function CampaignLocationsSection({
  locations,
  setLocations,
  currentTargets,
  proximityValue,
  onProximityChange,
  businessId,
  disabled = false,
  campaignName,
  excludedCriteriaIds,
}: CampaignLocationsSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deletingRow, setDeletingRow] = useState<LocationTargetRow | null>(null);
  // When editing a specific radius row, jump the picker straight to it (Radius tab).
  const [initialRadiusEdit, setInitialRadiusEdit] = useState<RadiusFormValue | null>(null);

  // Business's saved catalog (geo + radius) locations — the picker's checklist candidates.
  const { businessCandidates, radiusCandidates } = useBusinessLocationCandidates(businessId, dialogOpen);

  // Effective radius rows: the draft when edited, else the campaign's current
  // (possibly inherited) targets — mirrors CampaignRadiusTargets' editBase().
  const radiusRows = useMemo(
    () =>
      proximityValue != null
        ? proximityValue.map((v) => ({ address: v.address ?? undefined, radius_km: v.radius_km, latitude: v.latitude, longitude: v.longitude }))
        : currentTargets.map((t) => ({ address: t.address ?? undefined, radius_km: t.radius_km, latitude: t.latitude, longitude: t.longitude })),
    [proximityValue, currentTargets],
  );

  const radii = useMemo<RadiusFormValue[]>(() => {
    const out: RadiusFormValue[] = [];
    for (const r of radiusRows) {
      if (r.latitude != null && r.longitude != null) {
        out.push({
          lat: r.latitude,
          lng: r.longitude,
          radiusKm: r.radius_km,
          countryCode: null,
          formattedAddress: r.address ?? `${r.latitude}, ${r.longitude}`,
        });
      }
    }
    return out;
  }, [radiusRows]);

  const rows = useMemo<LocationTargetRow[]>(
    () => [
      ...locations.map((l) => ({ kind: 'geo' as const, key: `geo-${l.criteria_id}`, location: l })),
      ...radiusRows.map((r, i) => ({
        kind: 'radius' as const,
        key: `radius-${i}`,
        index: i,
        address: r.address,
        radiusKm: r.radius_km,
        lat: r.latitude,
        lng: r.longitude,
      })),
    ],
    [locations, radiusRows],
  );

  const { sortedRows, sort, cycleSort } = useLocationTargetSort(rows);

  const confirmDelete = () => {
    if (!deletingRow) return;
    if (deletingRow.kind === 'geo') {
      setLocations(locations.filter((l) => l.criteria_id !== deletingRow.location.criteria_id));
    } else {
      const base = proximityValue != null ? proximityValue : toInputList(currentTargets);
      onProximityChange(base.filter((_, i) => i !== deletingRow.index));
    }
    setDeletingRow(null);
  };

  const openAdd = () => {
    setInitialRadiusEdit(null);
    setDialogOpen(true);
  };

  const openEdit = (row: LocationTargetRow) => {
    // Radius rows jump the picker to that radius for editing; geo rows just open
    // the picker (geo targets are catalog selections, edited by re-picking).
    if (row.kind === 'radius' && row.lat != null && row.lng != null) {
      setInitialRadiusEdit({
        lat: row.lat,
        lng: row.lng,
        radiusKm: row.radiusKm,
        countryCode: null,
        formattedAddress: row.address ?? `${row.lat}, ${row.lng}`,
      });
    } else {
      setInitialRadiusEdit(null);
    }
    setDialogOpen(true);
  };

  // The picker needs a business; editable businessless campaigns keep the original
  // two-table UI (which has a list-based geo fallback).
  if (businessId == null && !disabled) {
    return (
      <div className="space-y-6">
        <CampaignTargetView locations={locations} setLocations={setLocations} disabled={disabled} campaignName={campaignName} excludedCriteriaIds={excludedCriteriaIds} />
        <CampaignRadiusTargets disabled={disabled} currentTargets={currentTargets} value={proximityValue} onChange={onProximityChange} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Locations</h3>
          <p className="mt-1 text-sm text-gray-700">Target geographic areas and radius zones for your ads.</p>
        </div>
        {!disabled && businessId != null && (
          <Button variant="outline" className="text-gray-900" onClick={openAdd}>
            <Plus className="mr-2 h-4 w-4" />
            Add Locations
          </Button>
        )}
      </div>

      <LocationTargetsTable
        sortedRows={sortedRows}
        sort={sort}
        onCycleSort={cycleSort}
        showActions={!disabled}
        onEdit={openEdit}
        onDelete={setDeletingRow}
      />

      {businessId != null && (
        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) setInitialRadiusEdit(null);
          }}
        >
          <DialogContent
            className="flex h-[85vh] max-h-[85vh] w-[92vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[1100px] [&>button]:top-4"
            aria-describedby={undefined}
          >
            <DialogHeader className="border-b border-gray-200 px-5 py-4">
              <DialogTitle className="truncate">
                {initialRadiusEdit ? 'Edit radius' : 'Add locations'}
                {campaignName ? ` for ${campaignName}` : ''}
              </DialogTitle>
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-hidden">
              <LocationMapPicker
                businessId={businessId}
                selected={locations}
                onChange={setLocations}
                excludedCriteriaIds={excludedCriteriaIds}
                candidates={businessCandidates}
                radiusCandidates={radiusCandidates}
                radii={radii}
                onRadiiChange={(rs) => onProximityChange(rs.map(toProximityInput))}
                initialRadiusEdit={initialRadiusEdit}
              />
            </div>
            <div className="flex justify-end border-t border-gray-200 px-5 py-3">
              <Button className="bg-[#290BD9] text-white hover:bg-[#2309C5]" onClick={() => setDialogOpen(false)}>
                Done
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <ConformationModal
        isOpen={deletingRow !== null}
        onClose={() => setDeletingRow(null)}
        onConfirm={confirmDelete}
        title="Remove Target"
        description={`Remove "${deletingRow ? locationTargetRowLabel(deletingRow) : ''}" from targeting?`}
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={false}
      />
    </div>
  );
}
